import React, {useContext, useMemo, useRef, useState} from 'react';
import {IconButton} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useRecoilValue} from "recoil";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {AgGridReact} from "ag-grid-react";
import {
    ColDef,
    ICellRendererParams,
    IServerSideDatasource,
    ValueFormatterParams
} from "ag-grid-community";
import dayjs from "dayjs";
import {DownloadRounded} from "@mui/icons-material";
import {AxiosError} from "axios";
import {downloadAttachment, fetchAttachmentsInfo } from '../services/http.services';
import {snackbarTypes} from "../../../../util/state/globalVariables";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import CustomLoader from "../../../../util/component/CustomLoader";
import {raDetailState} from "../state";

const AttachmentsInfo = (props: any) => {
    const {t} = useTranslation(['network', 'translation']);
    const [isSearching, setIsSearching] = useState(false);

    const { dispatchGlobalData } = useContext(GlobalContext);

    const raDetail = useRecoilValue(raDetailState);

    const gridRef = useRef<AgGridReact>(null);

    const [waitingForAxios, setWaitingForAxios] = useState(false);

    const defaultColDef = useMemo(() => {
        return {
            lockPinned: true,
            suppressSizeToFit: false,
        }
    }, []);

    const columnDefs = useMemo<ColDef[]>(() => [
        {headerName: t('raDetail.attachmentsInfo.headerName.documentTitle'), field: 'documentTitle'},
        {headerName: t('raDetail.attachmentsInfo.headerName.raNumber'), field: 'raNumber'},
        {
            headerName: t('raDetail.attachmentsInfo.headerName.uploadDate'),
            field: 'uploadDate',
            valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : ''),
        },
        {
            field: 'id',
            headerName: "",
            width: 70,
            cellRenderer: (params: ICellRendererParams) => {
                return (
                    <IconButton color="secondary" onClick={() => downloadFile(params.data)}>
                        <DownloadRounded/>
                    </IconButton>
                );
            }
        },
    ], [] );

    const downloadFile = async (event: any) => {
        setWaitingForAxios(true); 
        //console.log('LB setWaitingForAxios', waitingForAxios);
        try {
            const response: any = await downloadAttachment({ idDocument: event.id });
            let linkSource = '';
    
            if (response.file.includes('base64')) {
                linkSource = response.file;
            } else {
                if (response.fileName.includes('pdf')) {
                    linkSource = "data:application/pdf;base64," + response.file;
                } else if (response.fileName.includes('jpeg')) {
                    linkSource = "data:image/jpeg;base64," + response.file;
                } else {
                    linkSource = "data:image/png;base64," + response.file;
                }
            }
    
            const downloadLink = document.createElement('a');
            downloadLink.href = linkSource;
            downloadLink.download = response.fileName ? response.fileName : 'file';
            downloadLink.click();
        } catch (err: any) {
            dispatchGlobalData({
                type: 'SET_SNACKBAR',
                payload: {
                    snackbar: {
                        message: t('translation:error.tryLater'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        } finally {
            setTimeout(() => {
                setWaitingForAxios(false); // Imposta il loader su false dopo un ritardo
                //console.log('LB setWaitingForAxios (after request):', waitingForAxios);
            }, 1000); // Ritardo di 2 secondi (2000 millisecondi)
        
        }
    }

    const serverSideDatasource: IServerSideDatasource = useMemo(() => ({
        getRows(params) {
            fetchAttachmentsInfo({
                idRentalAgreement: encodeURIComponent(raDetail.idRentalAgreement),
                paramRequest: {
                    pagination:  Number(params.request.endRow) - Number(params.request.startRow),
                    startIndex:  params.request.startRow,
                    endIndex:  params.request.endRow
                }
                })
                .then((response: any) => {
                    params.success({
                        rowData: response.documents,
                        rowCount: response.params.totCount
                    })
                    setIsSearching(false);
                })
                .catch(() => {
                    params.fail();
                    setIsSearching(false);
                })
                .finally(() => {
                    setIsSearching(false);
                })
        }
    }), [isSearching]);

    return (
        <>
            <AgGridCustomTable
                gridRef={gridRef}
                rowModelType={'serverSide'}
                serverSideInfiniteScroll={true}
                serverSideDatasource={serverSideDatasource}
                columnDefs={columnDefs}
                gridOptions={
                    {
                        defaultColDef: defaultColDef,
                        onGridReady: (event) => {
                            event.api.sizeColumnsToFit()
                        },
                        // onRowClicked: (event: RowClickedEvent<any>) => routeChange(event)
                    }
                }
            />
            <CustomLoader loading={waitingForAxios} />
        </>
    );
}

export default AttachmentsInfo;